import React, { useState, useMemo, useEffect, useRef } from "react";
import { Modal, Form, Button, Checkbox, Radio, Row, Col } from "tea-component";
import { getLocale, useIntl } from "umi";
import {
  DataType,
  DownloadFormatModalProps,
  FileFormat,
  StatisticType,
  ISelectorDimensionConfig,
  ISelectorDimensionOption
} from "@/pages/dashboard/components/DownloadFormatModal/types";
import axios from "axios";
import moment from "moment";
import { downDataTip } from "@/pages/dashboard/utils/downDataTip";
import { useSortValue } from "@/pages/dashboard/utils/customHook";
import SelectorDimensionRadio from "@/pages/dashboard/components/OverallTableCo/components/SelectorDimensionRadio";

function DownloadFormatModal(props: DownloadFormatModalProps) {
  const { visible, onConfirm, onCancel, metricOptions = [], defaultValue,
    timeType = 'Day', showData = { valueOnly: true, showDod: true, showSdlw: true },
    tableName, showModalData = true, showModalDataStat = true, defaultFileType, requiredMetricCountToDownload, 
    customSelectorsDimensions,
    defaultSelectorValues,
  } = props;
  const { formatMessage } = useIntl();
  const isEN = getLocale() === 'en-US';

  const allValues = useMemo(() => metricOptions?.map((opt) => opt.value), [metricOptions]);
  const [metricValue, setMetricValue] = useState<any[]>(allValues);
  const [tempMetricValues, setTempMetricValues] = useState<any[]>(metricValue); // Temporary metric values

  const [dataValue, setDataValue] = useState<DataType>("value_only");
  const [fileFormat, setFileFormat] = useState<FileFormat>(defaultFileType || "excel");
  const [statisticsType, setStatisticsType] = useState<StatisticType>('data_only');
  const [selectorValues, setSelectorValues] = useState<Record<string, string>>({});

  const { setNoticeStatus } = useNotificationOfDownloadTable(tableName);
  const sortMetricsValue = useSortValue(metricOptions);

  const isConfirmButtonDisabled = metricOptions?.length > 0 && requiredMetricCountToDownload !== undefined && metricValue?.length < requiredMetricCountToDownload;


  useEffect(() => {
    const { valueOnly, showDod, showSdlw } = showData || {};
    const map = {
      "value_only": valueOnly,
      "show_dod": showDod,
      "show_sdlw": showSdlw,
    };
    // 分日期隐藏sdlw时重置
    if (!map[dataValue]) {
      setDataValue("value_only");
    }
  }, [dataValue, showData]);

  // 漏斗下载弹窗添加默认值勾选
  useEffect(() => {
    if (defaultValue) {
      setMetricValue(defaultValue);
      setTempMetricValues(defaultValue);
    } else {
      setMetricValue(allValues);
      setTempMetricValues(allValues);
    }
  }, [defaultValue, allValues]);

  const isVisibleRef = useRef<boolean>(false);
  // 仅在弹窗“打开瞬间”同步一次默认值；弹窗打开后不再受页面值影响
  useEffect(() => {
    const wasVisible = isVisibleRef.current;
    isVisibleRef.current = visible;
    if (!visible || wasVisible) return;
    if (!Array.isArray(customSelectorsDimensions) || customSelectorsDimensions.length === 0) return;
    const init: Record<string, string> = {};
    customSelectorsDimensions.forEach(d => {
      init[d.key] = (
        defaultSelectorValues?.[d.key]
        ?? d.defaultValue
        ?? d.options?.[0]?.value
        ?? ''
      ) as string;
    });
    setSelectorValues(init);
  }, [visible, customSelectorsDimensions, defaultSelectorValues]);

  return (
    <Modal
      // className="dtb-db-download-settings"
      visible={visible}
      onClose={onCancel}
      caption={formatMessage({ id: 'download_format_modal_title' })}
      size="xl"
    >
      <Modal.Body>
        <Form>
          <Form.Item label={formatMessage({ id: "download_format_modal_Metrics" })}>
            <Checkbox.Group className="dbd-check-list" value={metricValue} onChange={value => setMetricValue(value)} style={{ display: 'block' }}>
              <Row>
                <Col span={6}>
                  <Checkbox
                    name="all"
                    indeterminate={metricValue.length !== 0 && metricValue.length !== metricOptions.length}
                    onChange={(value) => {
                      const defaultValue = metricOptions?.find((item: any) => item?.disabled)?.value;
                      value ? setMetricValue(allValues) : setMetricValue(defaultValue ? [defaultValue] : []);
                    }}
                    value={metricValue.length === metricOptions.length}
                  >
                    {formatMessage({ id: "download_format_modal_All" })}
                  </Checkbox>
                </Col>
                {metricOptions.map((opt, index) => (
                  <Col key={index} span={6}>
                    <Checkbox disabled={opt?.disabled} key={opt.value} name={opt.value}>
                      {opt.text}
                    </Checkbox>
                  </Col>
                )) as any}
              </Row>
            </Checkbox.Group>
          </Form.Item>

          {Array.isArray(customSelectorsDimensions) && customSelectorsDimensions.length > 0 && (
            customSelectorsDimensions.map((dim: ISelectorDimensionConfig) => (
              <Form.Item
                key={dim.key}
                label={dim.labelEn && dim.labelCn ? (isEN ? dim.labelEn : dim.labelCn) : dim.key}
              >
                <SelectorDimensionRadio
                  value={selectorValues?.[dim.key] ?? dim.defaultValue ?? (dim.options || []).find((o: any) => o?.isAll)?.value ?? (dim.options || [])[0]?.value ?? 'all'}
                  className='dbd-select-radio-table-selector'
                  onChange={(value) => setSelectorValues(prev => ({ ...prev, [dim.key]: value }))}
                  options={dim.options as ISelectorDimensionOption[]}
                  hideLabel
                />
              </Form.Item>
            ))
          )}

          {showModalData && <Form.Item label={formatMessage({ id: "download_format_modal_Data" })}>
            <Radio.Group value={dataValue} onChange={(value) => setDataValue(value as DataType)}>
              {showData.valueOnly && (<Radio name="value_only">{formatMessage({ id: "download_format_modal_Value_Only" })}</Radio>)}
              {showData.showDod && (<Radio name="show_dod">{formatMessage({ id: `download_format_modal_Value_and_DoD_${timeType}` })}</Radio>)}
              {showData.showSdlw && (<Radio name="show_sdlw">{formatMessage({ id: `download_format_modal_Value_and_SDLW_${timeType}` })}</Radio>)}
            </Radio.Group>
          </Form.Item>}

          {showModalDataStat && <Form.Item label={formatMessage({ id: "download_format_modal_Data_Statistics" })}>
            <Radio.Group value={statisticsType} onChange={(value) => setStatisticsType(value as StatisticType)}>
              <Radio name="data_only">{formatMessage({ id: "download_format_modal_data_only" })}</Radio>
              <Radio name="more_statistics">{formatMessage({ id: `download_format_modal_more_statistics` })}</Radio>
            </Radio.Group>
          </Form.Item>}

          <Form.Item label={formatMessage({ id: "download_format_modal_File_Format" })}>
            {/*
                由于需求上要求 只有Excel选项时, 只展示文本, 所以用div
                但后期会增加CSV选项, 所以先保留 fileFormat状态, 以便后期改动时, 外面的下载逻辑可以不改变.
            */}
            <Radio.Group value={fileFormat} onChange={(value) => setFileFormat(value as FileFormat)}>
              <Radio className="tea-form__text" name="excel">{formatMessage({ id: "download_format_modal_Excel" })}</Radio>
              <Radio className="tea-form__text" name="csv">{formatMessage({ id: `download_format_modal_CSV` })}</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={isConfirmButtonDisabled} type="primary" onClick={() => {
          const metrics = sortMetricsValue(metricValue);
          const downloadPromise = onConfirm?.({
            metrics,
            dataType: dataValue,
            fileFormat: fileFormat,
            isDownloadStatistics: statisticsType !== "data_only",
            selectorValues,
          });
          if (downloadPromise) { // 有返回Promise对象时由内部处理下载过程
            // 展示正在下载中通知
            setNoticeStatus("loading");
            // 发起请求下载
            (async () => {
              try {
                const res = await downloadPromise;
                const fileName = `${tableName.replace(/\s/g, '_')} ${moment().format('YYYY_MM_DD HH_mm_ss')}`;

                await downloadFileWithFileFormat(res, fileName, {
                  onDownloadSuccess() {
                    setNoticeStatus("success");
                  },
                  onDownloadError() {
                    setNoticeStatus("error");
                  }
                }, fileFormat);

              } catch (e) {
                setNoticeStatus("error");
              }

            })();
          }
          setMetricValue(tempMetricValues);

          onCancel();
        }}>
          {formatMessage({ id: "download_format_modal_Confirm" })}
        </Button>
        <Button type="weak" onClick={() => {
          setMetricValue(tempMetricValues);
          if (Array.isArray(customSelectorsDimensions) && customSelectorsDimensions.length > 0) {
            const init: Record<string, string> = {};
            customSelectorsDimensions.forEach(d => {
              init[d.key] = (defaultSelectorValues?.[d.key] ?? d.defaultValue ?? 'all') as string;
            });
            setSelectorValues(init);
          }
          onCancel();
        }}>
          {formatMessage({ id: "download_format_modal_Cancel" })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default DownloadFormatModal;

export const downloadFileWithFileFormat = (response: ArrayBuffer, name: string, { onDownloadSuccess, onDownloadError }: any, fileFormat: FileFormat = 'excel') => {
  const fileType = fileFormat == 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv;charset=utf-8';
  const blob = new Blob([response], {
    type: fileType,
  });
  const linkNode = document.createElement('a');
  linkNode.download = name; // a标签的download属性规定下载文件的名称
  linkNode.style.display = 'none';
  linkNode.href = URL.createObjectURL(blob); // 生成一个Blob URL
  document.body.appendChild(linkNode);
  linkNode.click(); // 模拟在按钮上的一次鼠标单击
  if (blob.size < 100) {
    // downhide.destroy()
    // downDataTip('error',tipError)
    // setIsDownLoading(false)
    // return
    onDownloadError();
  }
  return axios({
    url: linkNode.href,
    method: "get",
    responseType: "blob",
    onDownloadProgress: progressEvent => {
      if (Math.round(progressEvent.loaded / progressEvent.total * 100) > 99) {
        onDownloadSuccess();
      }
    }
  }).then(() => {
  }).catch(e => {
    onDownloadError(e);
  }).finally(() => {
    URL.revokeObjectURL(linkNode.href); // 释放URL 对象
    document.body.removeChild(linkNode);
  });
};

export const useNotificationOfDownloadTable = (tableName: string) => {
  const { formatMessage } = useIntl();
  const notificationRef = useRef({ destroy: () => { } });
  const [status, setStatus] = useState('');
  const text = useMemo(() => {
    const textFun = (textKey: string) => {
      return (
        <>
          <span style={{ fontWeight: 600 }}>
            {tableName}
          </span>
          &nbsp;{formatMessage({ id: textKey })}
        </>
      );
    };
    return {
      loadingText: textFun('common_down_loading_text'),
      successText: textFun('common_down_success_text'),
      errorText: textFun('common_down_error_text'),
    };
  }, [tableName, getLocale()]);

  useEffect(() => {
    if (!status) return;
    notificationRef.current.destroy();
    notificationRef.current = downDataTip(status, text[`${status}Text` as keyof typeof text]);
  }, [status]);

  return { setNoticeStatus: setStatus };
};
